/**
 * The guided intake's first frame, fitted to the MEASURED head (INTAKE_GUIDED_FLOW §3.3, amended
 * 2026-10-03): a flattering head-and-a-little-shoulder portrait of ANY body, not World's exact
 * avatar-card frame.
 *
 * WHY IT DEPARTS FROM WORLD'S FRAME. World's headshot formula (`headshot-frame.ts`) assumes a
 * 1.75 m human: it reads the head bone's height and frames a fixed human head span. On a short,
 * big-headed body (the ActorCore kobold: 0.96 units tall, head and ears a third of it) that span
 * lands inside the head, and the first frame was an ear filling the window. So the frame is fitted
 * to the geometry the head actually owns — the skinned vertices dominated by the head subtree — plus
 * a shoulder allowance, and placed so the subject clears the step card.
 *
 * THE FIT, in world units on the declared facing axis, the camera level (β = π/2):
 *   - the box: the head box, its bottom lowered by SHOULDER_ALLOWANCE × the head's height;
 *   - the free band: from the top edge to the card's top edge, minus a margin; the crown sits
 *     CROWN_MARGIN of the viewport below the top edge, the box's bottom just above the card;
 *   - distance: the box's height fills the band, and its width fits the viewport with the same
 *     margin — whichever needs the camera further back wins;
 *   - laterally: centred on the head, not the body (a leaning head stays in frame).
 *
 * PURE: no three, no DOM. Depended on by: main.ts (the guided first frame), tests.
 */

export interface FitBox {
  readonly min: { readonly x: number; readonly y: number; readonly z: number };
  readonly max: { readonly x: number; readonly y: number; readonly z: number };
}

export interface FitView {
  /** Viewport size in CSS pixels. */
  readonly width: number;
  readonly height: number;
  /** Distance from the viewport's top to the card's top edge, CSS px (the card is bottom-anchored). */
  readonly cardTop: number;
}

/** The lens shape the viewport holds (structurally `HeadshotLens`). */
export interface FitLens {
  readonly target: { readonly x: number; readonly y: number; readonly z: number };
  readonly radius: number;
  readonly fovRad: number;
  readonly betaRad: number;
  readonly alphaRad: number;
}

/** The head's height again, below the head: the neck and a little shoulder. */
export const SHOULDER_ALLOWANCE = 0.45;
/** The crown sits this fraction of the viewport below the top edge. */
export const CROWN_MARGIN = 0.1;
/** Clear air between the subject and the card, and at the sides, as a fraction of the viewport. */
export const CARD_MARGIN = 0.03;
/** A portrait lens: narrow enough to flatter, wide enough to read as a face, not a telephoto. */
export const FIRST_FRAME_FOV = (28 * Math.PI) / 180;

/**
 * The lens for a head box on a facing azimuth `alphaRad` (ArcRotate: the camera at
 * target + radius·(cos α, 0, sin α) at β = π/2). Null when the band above the card is too thin to
 * frame anything (a window shorter than the card) — the caller falls back to the full body.
 */
export function fitHeadFrame(head: FitBox, alphaRad: number, view: FitView, fovRad = FIRST_FRAME_FOV, below = SHOULDER_ALLOWANCE): FitLens | null {
  const headH = head.max.y - head.min.y;
  if (!(headH > 0) || !(view.height > 0) || !(view.width > 0)) return null;
  const top = head.max.y;
  // `below`: how much of the box's own height to add under it (the shoulders for a head; 0 for a whole body).
  const bottom = head.min.y - headH * below;
  const boxH = top - bottom;
  // The band the subject may use, as fractions of the viewport height (0 = top edge).
  const bandTop = CROWN_MARGIN;
  const bandBottom = Math.min(1, view.cardTop / view.height) - CARD_MARGIN;
  const band = bandBottom - bandTop;
  if (band < 0.15) return null;
  const aspect = view.width / view.height;
  const tanHalf = Math.tan(fovRad / 2);
  // Width across the view: the head box's extent perpendicular to the viewing axis.
  const dirX = Math.cos(alphaRad);
  const dirZ = Math.sin(alphaRad);
  const halfX = (head.max.x - head.min.x) / 2;
  const halfZ = (head.max.z - head.min.z) / 2;
  const across = 2 * (Math.abs(dirZ) * halfX + Math.abs(dirX) * halfZ);
  const depthHalf = Math.abs(dirX) * halfX + Math.abs(dirZ) * halfZ;
  // Visible height at the box's front face: boxH must fill `band`; its width must fit with margins.
  const visForHeight = boxH / band;
  const visForWidth = across / ((1 - 2 * CARD_MARGIN * 4) * aspect);
  const visH = Math.max(visForHeight, visForWidth);
  const distance = visH / (2 * tanHalf);
  // Place the crown at bandTop: the view's centre line sits (0.5 − bandTop) of visH below the crown.
  const centreY = top - (0.5 - bandTop) * visH;
  const cx = (head.min.x + head.max.x) / 2;
  const cz = (head.min.z + head.max.z) / 2;
  return {
    target: { x: cx, y: centreY, z: cz },
    radius: distance + depthHalf,
    fovRad,
    betaRad: Math.PI / 2,
    alphaRad,
  };
}

/**
 * The tail question's frame (INTAKE_GUIDED_FLOW §3.7 `ask-tail`, Allen 2026-10-03: "frame the TAIL"): the
 * tail chain's world joints plus the hips, padded by SWAY_ROOM of the chain's length so a sway stays in
 * frame, fitted like the first frame on the given azimuth (the caller turns it to a back three-quarter).
 * Null when there are fewer than two points or nothing fits.
 */
export const SWAY_ROOM = 0.15;
export function fitTailFrame(points: readonly { x: number; y: number; z: number }[], alphaRad: number, view: FitView, fovRad = (30 * Math.PI) / 180, unordered = false): FitLens | null {
  if (points.length < 2 || !(view.height > 0) || !(view.width > 0)) return null;
  // The camera's own axes (level, ArcRotate azimuth α): across the view, and toward the camera.
  const toCam = { x: Math.cos(alphaRad), z: Math.sin(alphaRad) };
  const right = { x: toCam.z, z: -toCam.x };
  let length = 0;
  const across = points.map((p) => p.x * right.x + p.z * right.z);
  const depth = points.map((p) => p.x * toCam.x + p.z * toCam.z);
  points.forEach((p, k) => {
    const q = points[k - 1];
    if (q !== undefined) length += Math.hypot(p.x - q.x, p.y - q.y, p.z - q.z);
  });
  // `unordered`: points sampled from flesh (an unrigged tail's zone), not a chain — the room is a share of
  // the extent, since a walked length over scattered points means nothing.
  const extent = Math.max(Math.max(...across) - Math.min(...across), Math.max(...points.map((p) => p.y)) - Math.min(...points.map((p) => p.y)), Math.max(...depth) - Math.min(...depth));
  const pad = (unordered ? extent : length) * SWAY_ROOM;
  const uMin = Math.min(...across) - pad;
  const uMax = Math.max(...across) + pad;
  const top = Math.max(...points.map((p) => p.y)) + pad;
  const bottom = Math.min(...points.map((p) => p.y)) - pad;
  const dMid = (Math.min(...depth) + Math.max(...depth)) / 2;
  const dHalf = (Math.max(...depth) - Math.min(...depth)) / 2 + pad;
  const bandTop = CROWN_MARGIN;
  const band = Math.min(1, view.cardTop / view.height) - CARD_MARGIN - bandTop;
  if (band < 0.15) return null;
  const aspect = view.width / view.height;
  const visH = Math.max((top - bottom) / band, (uMax - uMin) / ((1 - 4 * CARD_MARGIN) * aspect));
  const distance = visH / (2 * Math.tan(fovRad / 2));
  const uMid = (uMin + uMax) / 2;
  const centreY = top - (0.5 - bandTop) * visH;
  return {
    target: { x: right.x * uMid + toCam.x * dMid, y: centreY, z: right.z * uMid + toCam.z * dMid },
    // Fitted at the chain's middle depth, not its near face: a tail runs AWAY from a back three-quarter
    // camera, and sizing it at the near face left it small; the sway room keeps the near joints in frame.
    radius: Math.max(distance, dHalf + distance * 0.25),
    fovRad,
    betaRad: Math.PI / 2,
    alphaRad,
  };
}
